import * as React from 'react';
import { useState } from 'react';
import { MapPin, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useWatch } from 'react-hook-form';
import type { UseFormReturn } from 'react-hook-form';

import { buildInitialAddressFromCustomerAddress } from '@klareospace/pharos-lib-front-utils';
import type { AddressInputInitialAddress } from '@klareospace/pharos-lib-front-utils';
import { Button, FormSectionHeader } from '@klareospace/pharos-lib-web-utils';
import { getFullName } from '@klareospace/pharos-lib-utils';
import type { CustomerWithResolvedRefs } from '@klareospace/pharos-lib-types';

import type { AddressBookEntryInput, CustomerFormInput } from '../schemas/customer.schema';
import { buildEmptyAddressEntry } from '../utils/customer-form.utils';
import { AddressCard } from './address-card';
import { AddressDialog } from './address-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressBookSectionProps {
  customer: CustomerWithResolvedRefs;
  form: UseFormReturn<CustomerFormInput>;
}

/** Which address the modal is currently open for — `null` when the modal is closed. */
type AddressBookDialogState = { mode: 'add' } | { index: number; mode: 'edit' } | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Only entries hydrated from a stored address (`sourceIndex` set) carry existing geocoding data. */
function resolveInitialAddress(
  entry: AddressBookEntryInput,
  customer: CustomerWithResolvedRefs,
): AddressInputInitialAddress | undefined {
  if (entry.sourceIndex === undefined) return undefined;
  return buildInitialAddressFromCustomerAddress(customer.addresses[entry.sourceIndex]);
}

/** Resolves the entry the modal should be seeded with for the current dialog state. */
function resolveDialogEntry(
  dialogState: AddressBookDialogState,
  watchedAddresses: AddressBookEntryInput[],
): AddressBookEntryInput | undefined {
  if (dialogState === null) return undefined;
  if (dialogState.mode === 'add') return { ...buildEmptyAddressEntry(), isDefault: watchedAddresses.length === 0 };
  return watchedAddresses[dialogState.index];
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Edit-mode address book: saved addresses as collapsed cards — they never expand inline — with a
 * count header and a "+ Add address" action. Adding or editing an address opens the
 * `AddressDialog` modal; the modal's own Save commits the entry into this form's `addresses`
 * field array, and Cancel/close discards it. An empty address book shows a dashed placeholder
 * instead of an auto-appended draft entry.
 */
export function AddressBookSection({ customer, form }: AddressBookSectionProps): React.JSX.Element {
  const { t } = useTranslation('customers');
  const { append, fields, remove, update } = useFieldArray({ control: form.control, name: 'addresses' });
  const watchedAddresses = useWatch({ control: form.control, name: 'addresses' }) ?? [];

  const [dialogState, setDialogState] = useState<AddressBookDialogState>(null);

  function handleSetDefault(index: number): void {
    fields.forEach((_, i) => {
      form.setValue(`addresses.${i}.isDefault`, i === index, { shouldDirty: true });
    });
  }

  function handleRemove(index: number): void {
    const wasDefault = form.getValues(`addresses.${index}.isDefault`) === true;
    const remainingCount = fields.length - 1;
    remove(index);

    if (remainingCount === 0 || !wasDefault) return;
    const newDefaultIndex = index < remainingCount ? index : 0;
    form.setValue(`addresses.${newDefaultIndex}.isDefault`, true, { shouldDirty: true });
  }

  function handleDialogSave(entry: AddressBookEntryInput): void {
    if (dialogState?.mode === 'edit') {
      update(dialogState.index, entry);
    } else {
      append(entry);
    }
    void form.trigger('addresses');
    setDialogState(null);
  }

  const contactFallbackName = getFullName(customer.firstName, customer.lastName);
  const dialogEntry = resolveDialogEntry(dialogState, watchedAddresses);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <FormSectionHeader icon={MapPin} title={t('form.sections.addressBookCount', { count: fields.length })} />
        <Button onClick={(): void => setDialogState({ mode: 'add' })} size="sm" type="button" variant="outline">
          <Plus className="h-4 w-4" />
          {t('addressBook.addAddress')}
        </Button>
      </div>

      {fields.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
          {t('addressBook.empty')}
        </div>
      ) : (
        <div className="space-y-3">
          {fields.map((field, index) => {
            const entry = watchedAddresses[index] ?? form.getValues(`addresses.${index}`);
            return (
              <AddressCard
                key={field.id}
                contactFallbackName={contactFallbackName}
                entry={entry}
                onEdit={(): void => setDialogState({ index, mode: 'edit' })}
                onRemove={(): void => handleRemove(index)}
                onSetDefault={(): void => handleSetDefault(index)}
              />
            );
          })}
        </div>
      )}

      {dialogState !== null && dialogEntry !== undefined && (
        <AddressDialog
          entry={dialogEntry}
          initialAddress={resolveInitialAddress(dialogEntry, customer)}
          mode={dialogState.mode}
          onClose={(): void => setDialogState(null)}
          onSave={handleDialogSave}
          open
        />
      )}
    </div>
  );
}
